import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { insuranceService } from 'src/services/insurance';

import { Checkbox } from '@components/ui/checkbox';

import { LandingHeader } from './components/LandingHeader';
import { LandingFooter } from './components/LandingFooter';
import { CtaBar } from './components/CtaBar';
import { PlanCard } from './components/PlanCard';
import { LandingPlan, mapMajorMedicalPlans, usd } from './plansData';

const OurPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<LandingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const didInit = useRef(false);

  useEffect(() => {
    let cancelled = false;
    insuranceService
      .getFullPlans()
      .then((all) => {
        if (cancelled) return;
        setPlans(mapMajorMedicalPlans(all));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Distinct deductible tiers, ascending.
  const tiers = useMemo(
    () => [...new Set(plans.map((p) => p.deductible))].sort((a, b) => a - b),
    [plans]
  );

  // Default-select the two lowest tiers once plans load (per spec: 1000 & 1500).
  useEffect(() => {
    if (!didInit.current && tiers.length) {
      didInit.current = true;
      setSelected(tiers.slice(0, 2));
    }
  }, [tiers]);

  const toggle = (tier: number) =>
    setSelected((s) =>
      s.includes(tier) ? s.filter((x) => x !== tier) : [...s, tier]
    );

  const visible = plans.filter((p) => selected.includes(p.deductible));

  return (
    <main className="min-h-screen bg-[#fbf7ef] text-[#23262C] antialiased">
      <LandingHeader />
      <div className="flex flex-col gap-[38px] max-[1200px]:gap-8">
        <section className="px-4 md:px-8">
          <div className="mx-auto max-w-[1180px] rounded-3xl bg-[#f5eee2]">
            <section className="px-4 pt-8 md:px-8 md:pt-12">
              <div className="mx-auto max-w-[1180px] text-center">
                <h1 className="!font-['Inter'] text-4xl font-light text-[#23262C] md:text-5xl">
                  Our Plans
                </h1>
                <p className="font-['Inter'] font-light text-base sm:text-lg leading-relaxed text-[#5C6068] mx-auto mt-4 max-w-2xl">
                  Explore flexible health coverage options designed for
                  individuals, families, and self-employed professionals.
                  Compare plans to find coverage that fits your needs, budget,
                  and lifestyle.
                </p>

                {!loading && !error && tiers.length > 0 && (
                  <>
                    <p className="!font-['Inter'] text-base sm:text-lg font-semibold text-[#23262C] mt-8">
                      Choose the plan deductible level you would like to see:
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
                      {tiers.map((tier) => (
                        <Checkbox
                          key={tier}
                          checked={selected.includes(tier)}
                          onCheckedChange={() => toggle(tier)}
                          label={usd(tier)}
                          className="!mt-0 self-center"
                          labelProps={{
                            className:
                              "!font-['Inter'] text-base sm:text-lg font-semibold text-[#23262C]",
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="px-4 py-10 md:px-8 md:py-14">
              {loading && (
                <p className="font-['Inter'] text-base sm:text-lg text-center text-[#9aa0a6]">
                  Loading plans…
                </p>
              )}
              {error && (
                <p className="font-['Inter'] text-base sm:text-lg text-center text-[#9aa0a6]">
                  Unable to load plans right now. Please try again later.
                </p>
              )}

              {!loading && !error && (
                <div className="mx-auto grid max-w-[1180px] grid-cols-1 gap-6 md:grid-cols-[repeat(auto-fill,minmax(290px,1fr))]">
                  <AnimatePresence mode="popLayout">
                    {visible.map((plan) => (
                      <motion.div
                        key={plan.id}
                        layout
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                      >
                        <PlanCard plan={plan} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}

              {!loading &&
                !error &&
                visible.length === 0 &&
                tiers.length > 0 && (
                  <p className="font-['Inter'] text-base sm:text-lg text-center text-[#9aa0a6] mt-6">
                    Select a deductible level above to view plans.
                  </p>
                )}
            </section>
          </div>
        </section>
        <CtaBar />
        <LandingFooter />
      </div>
    </main>
  );
};

export default OurPlansPage;
